'use client';

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from 'react';
import { useFileDownload, useImageLoad } from '@/hooks/files';
import {
  INVOCATION_STATUS,
  QUERY_STATUS,
  type QueryStatus,
} from '@/types/async-protocol';
import {
  downloadXYChartCsv,
  prepareXYChartImageSnapshot,
  renderXYChartImageSnapshot,
  type XYChartImageSnapshot,
} from './xychart-export';
import type { XYChartParsed } from './xychart-types';

const PNG_EXPORT_FAILURE_MESSAGE = '[Cmarkdownxychart] PNG export failed';

interface PendingImageExport {
  requestId: number;
  snapshot: XYChartImageSnapshot;
}

interface UseXYChartDownloadActionsInput {
  parsed: XYChartParsed | null;
  svgRef: RefObject<SVGSVGElement | null>;
}

export interface UseXYChartDownloadActionsResult {
  imageExportBusy: boolean;
  handleDownloadCsv: () => void;
  handleDownloadImage: () => void;
}

interface ImageExportEffectInput {
  pendingImageExport: PendingImageExport | null;
  processedImageExportIdRef: RefObject<number | null>;
}

function hasProcessedImageExport(input: ImageExportEffectInput): boolean {
  return (
    input.pendingImageExport != null &&
    input.processedImageExportIdRef.current === input.pendingImageExport.requestId
  );
}

function useXYChartImageExportFailureLogger(input: ImageExportEffectInput & {
  imageLoadStatus: QueryStatus;
  imageLoadError: unknown;
}): void {
  const {
    imageLoadError,
    imageLoadStatus,
    pendingImageExport,
    processedImageExportIdRef,
  } = input;

  useEffect(() => {
    if (pendingImageExport == null) return;
    if (hasProcessedImageExport({ pendingImageExport, processedImageExportIdRef })) {
      return;
    }
    if (imageLoadStatus !== QUERY_STATUS.Errored) return;

    processedImageExportIdRef.current = pendingImageExport.requestId;
    if (typeof console !== 'undefined') {
      console.error(PNG_EXPORT_FAILURE_MESSAGE, imageLoadError);
    }
  }, [
    imageLoadError,
    imageLoadStatus,
    pendingImageExport,
    processedImageExportIdRef,
  ]);
}

function useXYChartImageExportCompletion(input: ImageExportEffectInput & {
  imageLoadData: HTMLImageElement | null;
  imageLoadStatus: QueryStatus;
  fileDownload: ReturnType<typeof useFileDownload>;
}): void {
  const {
    fileDownload,
    imageLoadData,
    imageLoadStatus,
    pendingImageExport,
    processedImageExportIdRef,
  } = input;

  useEffect(() => {
    if (pendingImageExport == null) return;
    if (hasProcessedImageExport({ pendingImageExport, processedImageExportIdRef })) {
      return;
    }
    if (imageLoadStatus !== QUERY_STATUS.Success || !imageLoadData) {
      return;
    }

    processedImageExportIdRef.current = pendingImageExport.requestId;
    const pngDataUrl = renderXYChartImageSnapshot({
      snapshot: pendingImageExport.snapshot,
      image: imageLoadData,
    });
    if (!pngDataUrl) return;

    fileDownload.invoke(
      {
        url: pngDataUrl,
        filename: pendingImageExport.snapshot.filename,
      },
      {
        onError: (error) => {
          if (typeof console !== 'undefined') {
            console.error(PNG_EXPORT_FAILURE_MESSAGE, error);
          }
        },
      }
    );
  }, [
    fileDownload,
    imageLoadData,
    imageLoadStatus,
    pendingImageExport,
    processedImageExportIdRef,
  ]);
}

export function useXYChartDownloadActions({
  parsed,
  svgRef,
}: UseXYChartDownloadActionsInput): UseXYChartDownloadActionsResult {
  const [pendingImageExport, setPendingImageExport] =
    useState<PendingImageExport | null>(null);
  const imageExportRequestIdRef = useRef(0);
  const processedImageExportIdRef = useRef<number | null>(null);
  const imageLoad = useImageLoad(pendingImageExport?.snapshot.svgDataUrl ?? '');
  const fileDownload = useFileDownload();

  const imageExportBusy =
    (pendingImageExport != null && imageLoad.status === QUERY_STATUS.Loading) ||
    fileDownload.status === INVOCATION_STATUS.Loading;

  const handleDownloadCsv = useCallback((): void => {
    if (parsed == null) return;
    downloadXYChartCsv(parsed);
  }, [parsed]);

  const handleDownloadImage = useCallback((): void => {
    if (parsed == null || svgRef.current == null) return;

    const snapshot = prepareXYChartImageSnapshot({
      svgElement: svgRef.current,
      parsed,
    });
    if (!snapshot) return;

    const requestId = imageExportRequestIdRef.current + 1;
    imageExportRequestIdRef.current = requestId;
    processedImageExportIdRef.current = null;
    setPendingImageExport({ requestId, snapshot });
  }, [parsed, svgRef]);

  useXYChartImageExportFailureLogger({
    pendingImageExport,
    processedImageExportIdRef,
    imageLoadStatus: imageLoad.status,
    imageLoadError: imageLoad.error,
  });
  useXYChartImageExportCompletion({
    pendingImageExport,
    processedImageExportIdRef,
    imageLoadData: imageLoad.data,
    imageLoadStatus: imageLoad.status,
    fileDownload,
  });

  return { imageExportBusy, handleDownloadCsv, handleDownloadImage };
}
